// src/pages/trading-portal/VolatilityArbitrage/DeltaHedgingPage.js
// Delta Hedging page with 3-tab interface: Delta Monitors, Hedge Log, Risk Limits

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Grid,
    Switch,
    FormControlLabel,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    HedgeFrequency, HedgeFrequencyLabels,
    RiskMetricType, RiskMetricTypeLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const bodyCellSx = { fontFamily, color: '#1e293b', fontSize: '0.82rem' };

const EMPTY_MONITOR_FORM = {
    strategy_id: '',
    symbol: '',
    target_delta: 0,
    delta_tolerance: 0.05,
    hedge_frequency: '',
    auto_hedge_enabled: false,
};

const EMPTY_RISK_LIMIT_FORM = {
    strategy_id: '',
    metric_type: '',
    limit_value: '',
    warning_threshold: '',
};

const DeltaHedgingPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ==================== TAB 0: DELTA MONITORS ====================
    const [monitors, setMonitors] = useState([]);
    const [monitorsLoading, setMonitorsLoading] = useState(true);
    const [monitorsPage, setMonitorsPage] = useState(0);
    const [monitorsPerPage, setMonitorsPerPage] = useState(10);
    const [monitorsTotal, setMonitorsTotal] = useState(0);

    // ==================== TAB 1: HEDGE LOG ====================
    const [hedgeEvents, setHedgeEvents] = useState([]);
    const [hedgeLoading, setHedgeLoading] = useState(false);
    const [hedgePage, setHedgePage] = useState(0);
    const [hedgePerPage, setHedgePerPage] = useState(10);
    const [hedgeTotal, setHedgeTotal] = useState(0);
    const [hedgeFilterStrategyId, setHedgeFilterStrategyId] = useState('');
    const [hedgeFilterDateFrom, setHedgeFilterDateFrom] = useState('');
    const [hedgeFilterDateTo, setHedgeFilterDateTo] = useState('');

    // ==================== TAB 2: RISK LIMITS ====================
    const [riskLimits, setRiskLimits] = useState([]);
    const [riskLimitsLoading, setRiskLimitsLoading] = useState(false);
    const [riskLimitsPage, setRiskLimitsPage] = useState(0);
    const [riskLimitsPerPage, setRiskLimitsPerPage] = useState(10);
    const [riskLimitsTotal, setRiskLimitsTotal] = useState(0);

    // ==================== DIALOGS ====================
    // Add/Edit Delta Monitor Dialog
    const [monitorDialog, setMonitorDialog] = useState(false);
    const [monitorEditing, setMonitorEditing] = useState(null);
    const [monitorForm, setMonitorForm] = useState({ ...EMPTY_MONITOR_FORM });

    // Delete Confirmation Dialog
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Add/Edit Risk Limit Dialog
    const [riskLimitDialog, setRiskLimitDialog] = useState(false);
    const [riskLimitEditing, setRiskLimitEditing] = useState(null);
    const [riskLimitForm, setRiskLimitForm] = useState({ ...EMPTY_RISK_LIMIT_FORM });

    // ==================== FORMAT HELPERS ====================
    const formatNumber = (num) => num != null ? parseFloat(num).toLocaleString() : '-';
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, add, edit, del] = await Promise.all([
                hasPermission('tr-vae-delta-hedging', 'view'),
                hasPermission('tr-vae-delta-hedging', 'add'),
                hasPermission('tr-vae-delta-hedging', 'edit'),
                hasPermission('tr-vae-delta-hedging', 'delete'),
            ]);
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPerms();
    }, []);

    // ==================== TAB 0: DELTA MONITORS ====================
    const fetchMonitors = useCallback(async () => {
        if (!canView) return;
        setMonitorsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: monitorsPage + 1, per_page: monitorsPerPage });
            const res = await axiosInstance.get(`/api/volatility-arbitrage/delta-monitors?${params.toString()}`);
            if (res.data.success) {
                setMonitors(res.data.data || []);
                setMonitorsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch delta monitors');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch delta monitors');
        } finally {
            setMonitorsLoading(false);
        }
    }, [canView, monitorsPage, monitorsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchMonitors();
    }, [fetchMonitors, canView, activeTab]);

    // ==================== TAB 1: HEDGE LOG ====================
    const fetchHedgeEvents = useCallback(async () => {
        if (!canView) return;
        setHedgeLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: hedgePage + 1, per_page: hedgePerPage });
            if (hedgeFilterStrategyId) params.append('strategy_id', hedgeFilterStrategyId);
            if (hedgeFilterDateFrom) params.append('date_from', hedgeFilterDateFrom);
            if (hedgeFilterDateTo) params.append('date_to', hedgeFilterDateTo);
            const res = await axiosInstance.get(`/api/volatility-arbitrage/hedge-events?${params.toString()}`);
            if (res.data.success) {
                setHedgeEvents(res.data.data || []);
                setHedgeTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch hedge events');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch hedge events');
        } finally {
            setHedgeLoading(false);
        }
    }, [canView, hedgePage, hedgePerPage, hedgeFilterStrategyId, hedgeFilterDateFrom, hedgeFilterDateTo]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchHedgeEvents();
    }, [fetchHedgeEvents, canView, activeTab]);

    // ==================== TAB 2: RISK LIMITS ====================
    const fetchRiskLimits = useCallback(async () => {
        if (!canView) return;
        setRiskLimitsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: riskLimitsPage + 1, per_page: riskLimitsPerPage });
            const res = await axiosInstance.get(`/api/volatility-arbitrage/risk-limits?${params.toString()}`);
            if (res.data.success) {
                setRiskLimits(res.data.data || []);
                setRiskLimitsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch risk limits');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch risk limits');
        } finally {
            setRiskLimitsLoading(false);
        }
    }, [canView, riskLimitsPage, riskLimitsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchRiskLimits();
    }, [fetchRiskLimits, canView, activeTab]);

    // ==================== MONITOR ACTIONS ====================
    const handleOpenMonitorDialog = (monitor = null) => {
        if (monitor) {
            setMonitorEditing(monitor);
            setMonitorForm({
                strategy_id: monitor.strategy_id ?? '',
                symbol: monitor.symbol || '',
                target_delta: monitor.target_delta ?? 0,
                delta_tolerance: monitor.delta_tolerance ?? 0.05,
                hedge_frequency: monitor.hedge_frequency || '',
                auto_hedge_enabled: monitor.auto_hedge_enabled || false,
            });
        } else {
            setMonitorEditing(null);
            setMonitorForm({ ...EMPTY_MONITOR_FORM });
        }
        setMonitorDialog(true);
    };

    const handleMonitorFormChange = (field, value) => {
        setMonitorForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveMonitor = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                strategy_id: monitorForm.strategy_id ? Number(monitorForm.strategy_id) : null,
                symbol: monitorForm.symbol,
                target_delta: monitorForm.target_delta != null ? Number(monitorForm.target_delta) : 0,
                delta_tolerance: monitorForm.delta_tolerance != null ? Number(monitorForm.delta_tolerance) : 0.05,
                hedge_frequency: monitorForm.hedge_frequency || null,
                auto_hedge_enabled: monitorForm.auto_hedge_enabled,
            };
            let res;
            if (monitorEditing) {
                res = await axiosInstance.put(`/api/volatility-arbitrage/delta-monitors/${monitorEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/volatility-arbitrage/delta-monitors', payload);
            }
            if (res.data.success) {
                setMonitorDialog(false);
                setSnackbar({ open: true, message: `Delta monitor ${monitorEditing ? 'updated' : 'created'} successfully`, severity: 'success' });
                fetchMonitors();
            } else {
                setError(res.data.message || 'Failed to save delta monitor');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save delta monitor');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteMonitor = async () => {
        if (!deleteTarget) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.delete(`/api/volatility-arbitrage/delta-monitors/${deleteTarget.id}`);
            if (res.data.success) {
                setDeleteDialog(false);
                setDeleteTarget(null);
                setSnackbar({ open: true, message: 'Delta monitor deleted', severity: 'success' });
                fetchMonitors();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to delete monitor', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to delete monitor', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== RISK LIMIT ACTIONS ====================
    const handleOpenRiskLimitDialog = (limit = null) => {
        if (limit) {
            setRiskLimitEditing(limit);
            setRiskLimitForm({
                strategy_id: limit.strategy_id ?? '',
                metric_type: limit.metric_type || '',
                limit_value: limit.limit_value ?? '',
                warning_threshold: limit.warning_threshold ?? '',
            });
        } else {
            setRiskLimitEditing(null);
            setRiskLimitForm({ ...EMPTY_RISK_LIMIT_FORM });
        }
        setRiskLimitDialog(true);
    };

    const handleRiskLimitFormChange = (field, value) => {
        setRiskLimitForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveRiskLimit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                strategy_id: riskLimitForm.strategy_id ? Number(riskLimitForm.strategy_id) : null,
                metric_type: riskLimitForm.metric_type || null,
                limit_value: riskLimitForm.limit_value !== '' ? Number(riskLimitForm.limit_value) : null,
                warning_threshold: riskLimitForm.warning_threshold !== '' ? Number(riskLimitForm.warning_threshold) : null,
            };
            let res;
            if (riskLimitEditing) {
                res = await axiosInstance.put(`/api/volatility-arbitrage/risk-limits/${riskLimitEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/volatility-arbitrage/risk-limits', payload);
            }
            if (res.data.success) {
                setRiskLimitDialog(false);
                setSnackbar({ open: true, message: `Risk limit ${riskLimitEditing ? 'updated' : 'created'} successfully`, severity: 'success' });
                fetchRiskLimits();
            } else {
                setError(res.data.message || 'Failed to save risk limit');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save risk limit');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== RENDER: LOADING SKELETON ====================
    const renderSkeleton = (rows = 5) => (
        <Box sx={{ p: 3 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />
            ))}
        </Box>
    );

    // ==================== RENDER: TAB 0 - DELTA MONITORS ====================
    const renderDeltaMonitors = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Delta Monitors
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchMonitors}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button
                            startIcon={<MuiIcons.Add />}
                            variant="contained"
                            onClick={() => handleOpenMonitorDialog()}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Add Monitor
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {monitorsLoading ? renderSkeleton() : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={headCellSx}>Strategy ID</TableCell>
                                    <TableCell sx={headCellSx}>Symbol</TableCell>
                                    <TableCell sx={headCellSx}>Current Delta</TableCell>
                                    <TableCell sx={headCellSx}>Target Delta</TableCell>
                                    <TableCell sx={headCellSx}>Tolerance</TableCell>
                                    <TableCell sx={headCellSx}>Hedge Frequency</TableCell>
                                    <TableCell sx={headCellSx}>Auto-Hedge</TableCell>
                                    <TableCell sx={headCellSx}>Last Hedge At</TableCell>
                                    <TableCell sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {monitors.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <MuiIcons.TrackChanges sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                No delta monitors configured
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Create a delta monitor to track and auto-hedge positions
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : monitors.map((monitor) => (
                                    <TableRow key={monitor.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                                            {monitor.strategy_id ?? '-'}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{monitor.symbol || '-'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(monitor.current_delta)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(monitor.target_delta)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(monitor.delta_tolerance)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={HedgeFrequencyLabels[monitor.hedge_frequency] || monitor.hedge_frequency || '-'}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={monitor.auto_hedge_enabled ? 'Enabled' : 'Disabled'}
                                                size="small"
                                                color={monitor.auto_hedge_enabled ? 'success' : 'default'}
                                                variant="outlined"
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{formatDateTime(monitor.last_hedge_at)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleOpenMonitorDialog(monitor)}>
                                                            <MuiIcons.Edit sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => { setDeleteTarget(monitor); setDeleteDialog(true); }}>
                                                            <MuiIcons.Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!monitorsLoading && monitors.length > 0 && (
                    <TablePagination
                        component="div"
                        count={monitorsTotal}
                        page={monitorsPage}
                        onPageChange={(_, newPage) => setMonitorsPage(newPage)}
                        rowsPerPage={monitorsPerPage}
                        onRowsPerPageChange={(e) => { setMonitorsPerPage(parseInt(e.target.value, 10)); setMonitorsPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                    />
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 1 - HEDGE LOG ====================
    const renderHedgeLog = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Hedge Log
                </Typography>
                <Button
                    startIcon={<MuiIcons.Refresh />}
                    onClick={fetchHedgeEvents}
                    size="small"
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Strategy ID"
                            type="number"
                            size="small"
                            fullWidth
                            value={hedgeFilterStrategyId}
                            onChange={(e) => { setHedgeFilterStrategyId(e.target.value); setHedgePage(0); }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Date From"
                            type="date"
                            size="small"
                            fullWidth
                            value={hedgeFilterDateFrom}
                            onChange={(e) => { setHedgeFilterDateFrom(e.target.value); setHedgePage(0); }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Date To"
                            type="date"
                            size="small"
                            fullWidth
                            value={hedgeFilterDateTo}
                            onChange={(e) => { setHedgeFilterDateTo(e.target.value); setHedgePage(0); }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button
                            startIcon={<MuiIcons.FilterAltOff />}
                            onClick={() => { setHedgeFilterStrategyId(''); setHedgeFilterDateFrom(''); setHedgeFilterDateTo(''); setHedgePage(0); }}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {hedgeLoading ? renderSkeleton() : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={headCellSx}>Strategy ID</TableCell>
                                    <TableCell sx={headCellSx}>Hedge Type</TableCell>
                                    <TableCell sx={headCellSx}>Delta Before</TableCell>
                                    <TableCell sx={headCellSx}>Delta After</TableCell>
                                    <TableCell sx={headCellSx}>Shares Traded</TableCell>
                                    <TableCell sx={headCellSx}>Hedge Cost ($)</TableCell>
                                    <TableCell sx={headCellSx}>Execution Price ($)</TableCell>
                                    <TableCell sx={headCellSx}>Slippage ($)</TableCell>
                                    <TableCell sx={headCellSx}>Hedged At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {hedgeEvents.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <MuiIcons.ReceiptLong sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                No hedge events found
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Hedge events will appear here when positions are hedged
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : hedgeEvents.map((event) => (
                                    <TableRow key={event.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{event.strategy_id ?? '-'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{event.hedge_type || '-'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(event.delta_before)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(event.delta_after)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(event.shares_traded)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatPrice(event.hedge_cost)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatPrice(event.execution_price)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatPrice(event.slippage)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatDateTime(event.hedged_at)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!hedgeLoading && hedgeEvents.length > 0 && (
                    <TablePagination
                        component="div"
                        count={hedgeTotal}
                        page={hedgePage}
                        onPageChange={(_, newPage) => setHedgePage(newPage)}
                        rowsPerPage={hedgePerPage}
                        onRowsPerPageChange={(e) => { setHedgePerPage(parseInt(e.target.value, 10)); setHedgePage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                    />
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 2 - RISK LIMITS ====================
    const renderRiskLimits = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Risk Limits
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchRiskLimits}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button
                            startIcon={<MuiIcons.Add />}
                            variant="contained"
                            onClick={() => handleOpenRiskLimitDialog()}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Add Limit
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {riskLimitsLoading ? renderSkeleton() : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={headCellSx}>Strategy ID</TableCell>
                                    <TableCell sx={headCellSx}>Metric Type</TableCell>
                                    <TableCell sx={headCellSx}>Limit Value</TableCell>
                                    <TableCell sx={headCellSx}>Current Value</TableCell>
                                    <TableCell sx={headCellSx}>Warning Threshold</TableCell>
                                    <TableCell sx={headCellSx}>Breached</TableCell>
                                    <TableCell sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {riskLimits.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <MuiIcons.GppMaybe sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                No risk limits configured
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Set risk limits to monitor strategy exposure thresholds
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : riskLimits.map((limit) => (
                                    <TableRow key={limit.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{limit.strategy_id ?? 'Global'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={RiskMetricTypeLabels[limit.metric_type] || limit.metric_type || '-'}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(limit.limit_value)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(limit.current_value)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatNumber(limit.warning_threshold)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={limit.breached ? 'Breached' : 'OK'}
                                                size="small"
                                                sx={{
                                                    fontFamily,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    height: 24,
                                                    bgcolor: limit.breached ? '#fef2f2' : '#f0fdf4',
                                                    color: limit.breached ? '#dc2626' : '#16a34a',
                                                    border: `1px solid ${limit.breached ? '#fecaca' : '#bbf7d0'}`,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleOpenRiskLimitDialog(limit)}>
                                                            <MuiIcons.Edit sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!riskLimitsLoading && riskLimits.length > 0 && (
                    <TablePagination
                        component="div"
                        count={riskLimitsTotal}
                        page={riskLimitsPage}
                        onPageChange={(_, newPage) => setRiskLimitsPage(newPage)}
                        rowsPerPage={riskLimitsPerPage}
                        onRowsPerPageChange={(e) => { setRiskLimitsPerPage(parseInt(e.target.value, 10)); setRiskLimitsPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                    />
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: MONITOR DIALOG ====================
    const renderMonitorDialog = () => (
        <Dialog
            open={monitorDialog}
            onClose={() => setMonitorDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                {monitorEditing ? 'Edit Delta Monitor' : 'Add Delta Monitor'}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Strategy ID"
                            type="number"
                            fullWidth
                            size="small"
                            value={monitorForm.strategy_id}
                            onChange={(e) => handleMonitorFormChange('strategy_id', e.target.value)}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Symbol"
                            fullWidth
                            size="small"
                            value={monitorForm.symbol}
                            onChange={(e) => handleMonitorFormChange('symbol', e.target.value)}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Target Delta"
                            type="number"
                            fullWidth
                            size="small"
                            value={monitorForm.target_delta}
                            onChange={(e) => handleMonitorFormChange('target_delta', e.target.value)}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Delta Tolerance"
                            type="number"
                            fullWidth
                            size="small"
                            value={monitorForm.delta_tolerance}
                            onChange={(e) => handleMonitorFormChange('delta_tolerance', e.target.value)}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Hedge Frequency</InputLabel>
                            <Select
                                value={monitorForm.hedge_frequency}
                                label="Hedge Frequency"
                                onChange={(e) => handleMonitorFormChange('hedge_frequency', e.target.value)}
                                sx={{ fontFamily }}
                            >
                                {Object.entries(HedgeFrequencyLabels).map(([key, label]) => (
                                    <MenuItem key={key} value={key} sx={{ fontFamily }}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={monitorForm.auto_hedge_enabled}
                                    onChange={(e) => handleMonitorFormChange('auto_hedge_enabled', e.target.checked)}
                                    color="warning"
                                />
                            }
                            label="Auto-Hedge Enabled"
                            sx={{ fontFamily, '& .MuiFormControlLabel-label': { fontFamily, fontSize: '0.85rem' } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={() => setMonitorDialog(false)}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSaveMonitor}
                    variant="contained"
                    disabled={submitting}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                >
                    {submitting ? 'Saving...' : (monitorEditing ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: RISK LIMIT DIALOG ====================
    const renderRiskLimitDialog = () => (
        <Dialog
            open={riskLimitDialog}
            onClose={() => setRiskLimitDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                {riskLimitEditing ? 'Edit Risk Limit' : 'Add Risk Limit'}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Strategy ID (optional)"
                            type="number"
                            fullWidth
                            size="small"
                            value={riskLimitForm.strategy_id}
                            onChange={(e) => handleRiskLimitFormChange('strategy_id', e.target.value)}
                            helperText="Leave empty for global limit"
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                            FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.72rem' } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Metric Type</InputLabel>
                            <Select
                                value={riskLimitForm.metric_type}
                                label="Metric Type"
                                onChange={(e) => handleRiskLimitFormChange('metric_type', e.target.value)}
                                sx={{ fontFamily }}
                            >
                                {Object.entries(RiskMetricTypeLabels).map(([key, label]) => (
                                    <MenuItem key={key} value={key} sx={{ fontFamily }}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Limit Value"
                            type="number"
                            fullWidth
                            size="small"
                            value={riskLimitForm.limit_value}
                            onChange={(e) => handleRiskLimitFormChange('limit_value', e.target.value)}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Warning Threshold"
                            type="number"
                            fullWidth
                            size="small"
                            value={riskLimitForm.warning_threshold}
                            onChange={(e) => handleRiskLimitFormChange('warning_threshold', e.target.value)}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={() => setRiskLimitDialog(false)}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSaveRiskLimit}
                    variant="contained"
                    disabled={submitting}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                >
                    {submitting ? 'Saving...' : (riskLimitEditing ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: DELETE DIALOG ====================
    const renderDeleteDialog = () => (
        <Dialog
            open={deleteDialog}
            onClose={() => setDeleteDialog(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                Confirm Delete
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.85rem' }}>
                    Are you sure you want to delete this delta monitor? This action cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={() => setDeleteDialog(false)}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleDeleteMonitor}
                    variant="contained"
                    disabled={submitting}
                    sx={{
                        fontFamily,
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: '#ef4444',
                        '&:hover': { bgcolor: '#dc2626' },
                    }}
                >
                    {submitting ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== MAIN RENDER ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>
                    You do not have permission to view delta hedging. Required permission: <strong>tr-vae-delta-hedging</strong>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <MuiIcons.TrackChanges sx={{ fontSize: 28, color: ACCENT }} />
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '1.4rem', lineHeight: 1.2 }}>
                        Delta Hedging
                    </Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                        Monitor delta exposure, view hedge history, and manage risk limits
                    </Typography>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newVal) => setActiveTab(newVal)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            minHeight: 48,
                            color: '#64748b',
                            '&.Mui-selected': { color: ACCENT },
                        },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px 3px 0 0' },
                    }}
                >
                    <Tab icon={<MuiIcons.TrackChanges sx={{ fontSize: 18 }} />} iconPosition="start" label="Delta Monitors" />
                    <Tab icon={<MuiIcons.ReceiptLong sx={{ fontSize: 18 }} />} iconPosition="start" label="Hedge Log" />
                    <Tab icon={<MuiIcons.GppMaybe sx={{ fontSize: 18 }} />} iconPosition="start" label="Risk Limits" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderDeltaMonitors()}
            {activeTab === 1 && renderHedgeLog()}
            {activeTab === 2 && renderRiskLimits()}

            {/* Dialogs */}
            {renderMonitorDialog()}
            {renderRiskLimitDialog()}
            {renderDeleteDialog()}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ fontFamily, fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DeltaHedgingPage;
